import { useMemo } from 'react';
import { useCheckPaymentExpiration, useGetPaymentMethods } from '~/domain/Payments';
import { useSelectedOffice } from '~/hooks/useSelectedOffice';

/**
 * Returns whether the effective office can pay — i.e. it is pre-approved, or it has a
 * selected, non-expired payment method (bank accounts are always treated as valid).
 * Single source of truth for gating value-added features (invite / auto-confirm) that
 * require payment.
 *
 * Gating is driven by the **offer owner** (`officeId`) and, unlike the payment-reminder
 * banner, must NOT exempt parent offices: a parent office posting for itself without a
 * payment method should still be gated. We therefore evaluate the effective office's own
 * payment state directly instead of reusing `useCheckPaymentExpiration`'s `isInvalidPayment`,
 * which intentionally exempts parent offices for the notification banner (GOTU-2479) and is
 * derived from the sidebar-selected office rather than the offer owner.
 */
export const useHasValidPaymentMethod = (officeId?: string): boolean => {
  const office = useSelectedOffice(officeId);
  const isPreApproved = Boolean(office?.is_pre_approved);

  // Always resolve payments for the offer owner; only skip when pre-approved (payment moot).
  const { paymentMethods } = useGetPaymentMethods(officeId, {
    enabled: !isPreApproved,
  });

  // Reuse the shared expiry helper so card-expiry rules live in exactly one place.
  const { checkPaymentExpiration } = useCheckPaymentExpiration(officeId);

  return useMemo(() => {
    if (isPreApproved) {
      return true;
    }

    const validPayments = paymentMethods.filter(
      (payment) => payment.status !== 'pending',
    );
    if (!validPayments.length) {
      return false;
    }

    // A non-pending method exists, but validity hangs on the *selected* one. If none is
    // selected, `checkPaymentExpiration(undefined)` reports expired → invalid.
    const selectedPayment = paymentMethods.find((payment) => payment.is_selected);
    return !checkPaymentExpiration(selectedPayment).isExpired;
  }, [isPreApproved, paymentMethods, checkPaymentExpiration]);
};
